import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import ModerationLabel from '../ModerationLabel/ModerationLabel';
import CreatorComponent from '../../../Content/CreatorComponent/CreatorComponent';
import {
    CommunityContext,
    PageContext,
} from '../../../../contexts/ContextCreator';
import { constructCn } from '../../../../utilities/helperFunctions';
import { getFormattedTimestamp } from '../../../Utilities/TimeFunctions';
import SeeRepliesLink from '../SeeRepliesLink/SeeRepliesLink';
import {
    truncateAndLinkifyCommentContent,
    removeTagsFromString,
    linkify,
} from '../../../../utilities/ui_utilities';

import CommentBox from '../CommentBox';
import ReactionBar from '../../ReactionBar/ReactionBar';
import ModerationMenu from '../../ModerationMenu/ModerationMenu';

import './Comment.scss';
import {
    emitEngagement,
    objectTypes,
} from '../../../../utilities/getStreamAnalytics';
import PostBoxContainer from '../../../UnifiedPostBox/PostBoxContainer/PostBoxContainer';

/**
 * Comment component is used for both comments on content and replies on comments.
 * @param commentHash
 * @param nestDepth
 * @param location
 * @param expandMore - expands truncated comment
 * @param handleLinkClickMetricEmit - emits metric for link clicked
 * @returns {JSX.Element}
 * @constructor
 */

const Comment = ({
    commentHash,
    nestDepth,
    location,
    characterLimit,
    expandMore,
    handleLinkClickMetricEmit,
    handlePostAndJoin,
    submitButtonText,
}) => {
    const [expandComment, setExpandComment] = useState(false);

    const communityCtx = useContext(CommunityContext);
    const {
        isLoggedIn,
        wrapAuth,
        pageProps: {
            session: { id, name },
        },
    } = useContext(PageContext);
    const {
        getContent,
        pageProps,
        submitComment,
        openedCommentHash,
        userCommunityData,
    } = communityCtx;
    const { session } = pageProps;
    const [showingEditBox, setShowingEditBox] = useState(false);
    const comment = getContent(commentHash);
    const content = getContent(comment.parentContentHash);

    const isNested = nestDepth > 0;
    const replyCount = comment.commentCount;
    const childComments = (comment.comments || []).map((c) => {
        return (
            <Comment
                key={c.hash}
                commentHash={c.hash}
                nestDepth={nestDepth + 1}
                location={location}
                expandMore={expandMore}
                handleLinkClickMetricEmit={handleLinkClickMetricEmit}
                handlePostAndJoin={handlePostAndJoin}
                submitButtonText={submitButtonText}
            />
        );
    });

    // if we're nested 3 deep and have replies - show "see other replies" (links to single comment page)
    // otherwise show child comments
    // single comment page url /:contentType/:contentHash/:commentHash
    const url = content?.group?.groupUrl
        ? `${content?.group.groupUrl}${content?.contentUrl}/${commentHash}`
        : `${content.contentUrl}/${commentHash}`;
    const replies =
        nestDepth >= 3 && replyCount ? (
            <SeeRepliesLink
                url={url}
                content={content}
                commentHash={commentHash}
            />
        ) : (
            childComments
        );

    const commentCn = constructCn(
        'component-Community-CommentBox-Comment',
        isNested && 'nested-margin',
        `comment-${nestDepth}`
    );

    const commentWithTagsRemoved = removeTagsFromString(comment.comment);
    const truncatedComment = truncateAndLinkifyCommentContent(
        `${commentWithTagsRemoved || ''}`,
        characterLimit,
        false
    );
    const handleOnEditClick = () => {
        setShowingEditBox(true);
    };

    const handleOnEditCanceled = () => {
        setShowingEditBox(false);
    };

    const isReply = comment.parentType === 'community-comment';
    const isFeed =
        location === 'masterFeed' ||
        location === 'groupFeed' ||
        location === 'homePage';
    const showMore =
        isFeed &&
        !isReply &&
        comment.comment &&
        commentWithTagsRemoved.length > characterLimit;

    useEffect(() => {
        setExpandComment(expandMore);
    }, [setExpandComment, expandMore]);

    const handleExpandClick = (hash) => {
        if (!isLoggedIn()) return;
        handleLinkClickMetricEmit('more-comment-click');
        emitEngagement(
            { id, name },
            {
                label: 'seeMoreOfComment',
                score: 1,
                content: {
                    foreign_id: `${objectTypes()[content.type]}:${
                        content.hash
                    }`,
                    comment: {
                        label: `${objectTypes()[content.type]}:${content.hash}`,
                        id: `CommunityComment:${hash}`,
                    },
                },
            }
        );
        setExpandComment(true);
    };

    const renderCommentInput = () => {
        if (openedCommentHash !== commentHash || location === 'contributions')
            return null;
        const { username, logo } = session;
        const authorImage = {
            src: logo || '/v1/avatar_black_160_vtg7lw.svg',
            alt: `${username || 'default'} img`,
        };
        const isVip = !!userCommunityData?.isVip;
        const placeholderText = 'Your reply...';

        return (
            <div id="commentList-comment-input">
                <CommentBox
                    parentHash={commentHash}
                    placeholderText={placeholderText}
                    authorImage={authorImage}
                    isVip={isVip}
                    showExpanded
                    onSubmit={(hash, commentText, isAnon) => {
                        if (handlePostAndJoin) {
                            handlePostAndJoin(hash, commentText, isAnon);
                        } else {
                            submitComment(hash, commentText, isAnon);
                        }
                    }}
                    submitButtonText={submitButtonText}
                />
            </div>
        );
    };

    const renderReactionBar = () => {
        const redirectToPage = location === 'contributions';
        return (
            <ReactionBar
                hash={comment.hash}
                expandCommentCallback={setExpandComment}
                location={location}
                redirectToPage={redirectToPage}
            />
        );
    };
    const edited = comment.moderationOption?.status?.type === 'edit';
    const dateModified = edited
        ? comment.moderationOption.status.lastEditDate
        : comment.contentDate;
    const authMetrics = {
        login_trigger: 'comment',
        community_content_key: `${content.type}-${content.hash}`,
        community_on_comment: content.type === 'community-comment',
    };

    // deleted tag if comment is deleted
    // otherwise build comment body
    const commentBody =
        comment.moderationOption?.status?.type === 'delete' ? (
            <ModerationLabel type="delete" date={comment.contentDate} />
        ) : (
            <>
                <div className="comment-creatorContainer">
                    <CreatorComponent
                        author={comment.author}
                        contentHash={comment.hash}
                        edited={edited}
                        formattedTimestamp={getFormattedTimestamp(dateModified)}
                        className="comment-author"
                    />
                    <ModerationMenu
                        hash={comment.hash}
                        location={location}
                        handleOnEditClick={handleOnEditClick}
                        editboxShowing={showingEditBox}
                    />
                </div>
                <div className="comment-text">
                    {showingEditBox ? (
                        <PostBoxContainer
                            description={commentWithTagsRemoved}
                            type={comment.type}
                            editMode={showingEditBox}
                            handleOnEditCanceled={handleOnEditCanceled}
                            hash={comment.hash}
                            isAnon={comment.isAnon}
                            isComment
                            commentDepth={nestDepth}
                        >
                            {' '}
                        </PostBoxContainer>
                    ) : (
                        <span>
                            {expandComment || !showMore
                                ? linkify(commentWithTagsRemoved)
                                : linkify(truncatedComment)}
                            {showMore && !expandComment
                                ? wrapAuth(
                                      `comment-${comment.hash}`,
                                      <span
                                          className="comment-more-link"
                                          role="link"
                                          tabIndex="0"
                                          onClick={() =>
                                              handleExpandClick(comment.hash)
                                          }
                                          onKeyPress={handleExpandClick}
                                      >
                                          ...More
                                      </span>,
                                      authMetrics
                                  )
                                : null}
                        </span>
                    )}
                </div>
                {renderReactionBar()}
                {renderCommentInput()}
            </>
        );

    return (
        <div className={commentCn}>
            {commentBody}
            {replies}
        </div>
    );
};

Comment.propTypes = {
    commentHash: PropTypes.string.isRequired,
    nestDepth: PropTypes.number.isRequired,
    location: PropTypes.oneOf([
        'masterFeed',
        'miniFeed',
        'groupFeed',
        'singleContent',
        'contributions',
        'homePage',
    ]).isRequired,
    characterLimit: PropTypes.number,
    expandMore: PropTypes.bool,
    handleLinkClickMetricEmit: PropTypes.func,
    handlePostAndJoin: PropTypes.func,
    submitButtonText: PropTypes.string,
};

Comment.defaultProps = {
    expandMore: false,
    handleLinkClickMetricEmit: () => {},
    handlePostAndJoin: null,
    submitButtonText: 'Post',
    characterLimit: 150,
};

Comment.displayName = 'Comment';

export default Comment;
